import { FormInput } from '@/components/form'
import CompanySelection from '@/components/selection/CompanySelection'
import { useNotification, useRouteQuery } from '@/hooks'
import { RootState } from '@/redux-store'
import {
  DialogTitle,
  Divider,
  DialogContent,
  Drawer,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  AlertTitle,
  Alert
} from '@mui/material'
import dayjs from 'dayjs'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { Balance, Company, getBalanceTransactionSchema, useBalanceTransactionCreateMutation } from '@/@data'
import { useQuery } from '@tanstack/react-query'
import { yupResolver } from '@hookform/resolvers/yup'
import FormFileInput from '@/components/form/FormFileInput'

export const CreateBalanceTransaction = ({
  open,
  handleClose,
  refetch
}: {
  open: boolean
  handleClose: () => void
  refetch: () => void
}) => {
  const { __, __n } = useTranslator()
  const { searchQuery, updateQuery, serializeQuery } = useRouteQuery()
  const monthParam = searchQuery.get('month') || new Date().toISOString().substring(0, 7)
  const { id } = useParams()
  const { user } = useSelector((state: RootState) => state.userReducer)
  const n = useNotification()

  const companyId_create = searchQuery.get('companyId_create')

  const { mutate: createTransaction } = useBalanceTransactionCreateMutation()

  const params = useMemo(() => {
    return {
      company_id: id ? Number(id) : companyId_create,
      year: monthParam.substring(0, 4),
      month: monthParam.substring(5, 7)
    }
  }, [companyId_create, monthParam])

  const { data: dataBalanceTransactionDept } = useQuery({
    ...Balance.getBalanceDeptByCompanyId(params),
    enabled: open && (!!companyId_create || !!id)
  })

  const { data: dataCompany } = useQuery({
    ...Company.objects.paginateQueryFactory(
      serializeQuery({
        ids: Array.isArray(companyId_create) ? companyId_create : [companyId_create]
      })
    ),
    enabled: !!companyId_create && (Array.isArray(companyId_create) ? companyId_create.length > 0 : true)
  })

  const nameCompany = dataCompany?.data[0]?.name

  const remaining: any = useMemo(() => {
    return {
      voucher: dataBalanceTransactionDept?.data?.voucher_no_tax?.remaining,
      voucher_tax: dataBalanceTransactionDept?.data?.voucher_tax?.remaining,
      debt_disbursement: dataBalanceTransactionDept?.data?.return_wages_disbursement?.remaining,
      different: dataBalanceTransactionDept?.data?.different?.remaining
    }
  }, [id, monthParam, companyId_create, dataBalanceTransactionDept])

  const methods = useForm<any>({
    defaultValues: {
      month: '',
      company_id: undefined,
      voucher: 0,
      voucher_tax: 0,
      debt_disbursement: 0,
      different: 0,
      file: null,
      creator: user?.first_name
    },
    mode: 'onChange',
    resolver: yupResolver(getBalanceTransactionSchema(__, remaining))
  })

  const [currentDate, setCurrentDate] = useState<Date>(monthParam ? dayjs(monthParam).toDate() : new Date())

  const [showError, setShowError] = useState<boolean>(false)

  useEffect(() => {
    const newDate = monthParam ? dayjs(monthParam).toDate() : new Date()

    setCurrentDate(newDate)

    methods.reset({
      ...methods.getValues(),
      month: monthParam,
      company_id: id ? Number(id) : companyId_create
    })
  }, [monthParam, id, companyId_create])

  const currentMonth = useMemo(() => {
    return dayjs(currentDate).format('MMMM - YYYY')
  }, [currentDate])

  useEffect(() => {
    updateQuery({ companyId_create: undefined })
  }, [])

  const onSubmit = async (data: any) => {
    let payload = data

    delete payload.creator

    if (id) {
      payload.company_id = Number(id)
    }

    if (data.file instanceof FileList && data.file.length > 0) {
      const formData = new FormData()

      formData.append('file', data.file[0])

      Object.keys(data).forEach(key => {
        if (key !== 'file') {
          formData.append(key, data[key])
        }
      })

      payload = formData
    }

    createTransaction(
      { data: payload },
      {
        onSuccess: () => {
          n.success(__('Create balance transaction successfully'))
          handleClose()
          methods.reset({
            month: searchQuery.get('month'),
            company_id: id ? Number(id) : undefined,
            voucher: 0,
            voucher_tax: 0,
            debt_disbursement: 0,
            different: 0,
            file: null,
            creator: user?.first_name
          })
          refetch()
        },
        onError: error => {
          n.error(error.message)
        }
      }
    )
  }

  const values = [
    methods.watch('voucher') || 0,
    methods.watch('voucher_tax') || 0,
    methods.watch('debt_disbursement') || 0,
    methods.watch('different') || 0
  ]

  const total = values.reduce((sum, val) => sum + Number(val), 0)

  const receiveDifferent = Number(methods.watch('different') || 0)

  const employerPercentage = Number(
    (
      (receiveDifferent /
        Number(
          dataBalanceTransactionDept?.data?.different?.reconciled
            ? dataBalanceTransactionDept?.data?.different?.reconciled
            : 0
        )) *
      100
    ).toFixed(2)
  )

  const liftPercentage = Number((100 - employerPercentage).toFixed(2))

  const wageDisbursementDifference = Number(dataBalanceTransactionDept?.data?.different?.reconciled)

  useEffect(() => {
    if (
      dataBalanceTransactionDept?.data === null ||
      (!dataBalanceTransactionDept?.data?.is_allow_create_balance && !!companyId_create)
    ) {
      setShowError(true)
    } else {
      setShowError(false)
    }
  }, [dataBalanceTransactionDept, showError])

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      closeAfterTransition={false}
      anchor='right'
      variant='temporary'
      sx={{
        '& .MuiDrawer-paper': {
          width: '1100px'
        }
      }}
    >
      <DialogTitle className='flex justify-between items-center p-4'>
        <Typography className='text-[18px] leading-[28px] font-medium'>{`${__('Create Balance Transaction')}  ${currentMonth}`}</Typography>
        <Button className='w-6 h-6' onClick={handleClose} variant='text' color='inherit'>
          <i className='tabler-x w-6 h-6' />
        </Button>
      </DialogTitle>
      <Divider />

      <DialogContent className='p-6'>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} className='flex flex-col gap-6'>
            {showError && (
              <Alert
                severity='error'
                sx={{
                  mb: 2,
                  '& .MuiAlert-message': {
                    width: '100%'
                  }
                }}
              >
                <AlertTitle>{__('Error')}</AlertTitle>
                <div className='flex flex-col gap-1'>
                  <div className='flex flex-col'>
                    <span className='ml-2'>
                      {__(
                        `${nameCompany} is not yet eligible to create a transaction balance due to a pending request`
                      )}
                    </span>
                  </div>
                </div>
              </Alert>
            )}
            <Box className='flex flex-col gap-6'>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    name='company_id'
                    control={methods.control}
                    render={({ field }) => (
                      <CompanySelection
                        {...field}
                        label={__('Company')}
                        onChange={company_id => {
                          updateQuery({ companyId_create: company_id })
                        }}
                        required
                        disabled={!!id}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormInput
                    name='creator'
                    label={__('Creator')}
                    className='rounded-full w-full'
                    required
                    control={methods.control}
                    placeholder={__('Enter Creator')}
                    disabled
                  />
                </Grid>
              </Grid>
              <FormFileInput
                name='file'
                label={__('File')}
                control={methods.control}
                placeholder={__('Import File')}
                fullWidth
                required
                onClear={() => methods.setValue('file', null)}
              />

              <Box className='bg-white rounded-[24px] border border-gray-200'>
                <div className='overflow-x-auto'>
                  <table className='w-full border-collapse'>
                    <thead>
                      <tr className='border-b'>
                        <th className='p-6 text-left text-[13px] font-medium leading-[24px] space-[0.2px] uppercase'>
                          #
                        </th>
                        <th className='p-6 text-left text-[13px] font-medium leading-[24px] space-[0.2px] uppercase'>
                          {__('TYPE')}
                        </th>
                        <th className='p-6 text-left text-[13px] font-medium leading-[24px] space-[0.2px] uppercase'>
                          {__('EWA DISBURSEMENT')}
                        </th>
                        <th className='p-6 text-left text-[13px] font-medium leading-[24px] space-[0.2px] uppercase'>
                          {__('RECONCILED')}
                        </th>
                        <th className='p-6 text-left text-[13px] font-medium leading-[24px] space-[0.2px] uppercase'>
                          {__('REMAINING')}
                        </th>
                        <th className='p-6 text-left text-[13px] font-medium leading-[24px] space-[0.2px] uppercase'>
                          {__('RECEIVE')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className='border-b border-gray-100 '>
                        <td className='p-6 text-[15px] font-normal leading-[22px] '>1</td>
                        <td className='p-6 text-[15px] font-normal leading-[22px] '>{__('Voucher')}</td>
                        <td className='p-6 text-left text-[15px] font-normal leading-[22px]'>
                          {dataBalanceTransactionDept
                            ? __n(dataBalanceTransactionDept?.data?.voucher_no_tax?.ewa_disbursement, 'đ')
                            : '-'}
                        </td>
                        <td className='p-6 text-left text-[15px] font-normal leading-[22px]'>
                          {dataBalanceTransactionDept
                            ? __n(dataBalanceTransactionDept?.data?.voucher_no_tax?.reconciled, 'đ')
                            : '-'}
                        </td>
                        <td className='p-6 text-left text-[15px] font-normal leading-[22px]'>
                          {dataBalanceTransactionDept
                            ? __n(dataBalanceTransactionDept?.data?.voucher_no_tax?.remaining, 'đ')
                            : '-'}
                        </td>
                        <td className='p-6'>
                          <FormInput
                            name='voucher'
                            className='rounded-full w-full'
                            control={methods.control}
                            placeholder={__('Enter Value')}
                            disabled={!methods.getValues('company_id')}
                          />
                        </td>
                      </tr>
                      <tr className='border-b border-gray-100 '>
                        <td className='p-6 text-[15px] font-normal leading-[22px] '>2</td>
                        <td className='p-6 text-[15px] font-normal leading-[22px] '>{__('Voucher Tax')}</td>
                        <td className='p-6 text-left text-[15px] font-normal leading-[22px]'>
                          {dataBalanceTransactionDept
                            ? __n(dataBalanceTransactionDept?.data?.voucher_tax?.ewa_disbursement, 'đ')
                            : '-'}
                        </td>
                        <td className='p-6 text-left text-[15px] font-normal leading-[22px]'>
                          {dataBalanceTransactionDept
                            ? __n(dataBalanceTransactionDept?.data?.voucher_tax?.reconciled, 'đ')
                            : '-'}
                        </td>
                        <td className='p-6 text-left text-[15px] font-normal leading-[22px]'>
                          {dataBalanceTransactionDept
                            ? __n(dataBalanceTransactionDept?.data?.voucher_tax?.remaining, 'đ')
                            : '-'}
                        </td>
                        <td className='p-6'>
                          <FormInput
                            name='voucher_tax'
                            className='rounded-full w-full'
                            control={methods.control}
                            placeholder={__('Enter Value')}
                            disabled={!methods.getValues('company_id')}
                          />
                        </td>
                      </tr>
                      <tr className=' border-gray-100'>
                        <td className='px-6 py-4 text-[15px] font-normal leading-[22px] '>3</td>
                        <td className='px-6 py-4 text-[15px] font-normal leading-[22px] '>
                          {__('Return Wages Disbursement')}
                        </td>
                        <td className='px-6 py-4 text-left text-[15px] font-normal leading-[22px] flex items-center mt-2 gap-1'>
                          {dataBalanceTransactionDept
                            ? __n(dataBalanceTransactionDept?.data?.return_wages_disbursement?.ewa_disbursement, 'đ')
                            : '-'}
                          {dataBalanceTransactionDept?.data?.return_wages_disbursement?.ewa_disbursement >
                          dataBalanceTransactionDept?.data?.return_wages_disbursement?.reconciled ? (
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='23'
                              height='22'
                              viewBox='0 0 23 22'
                              fill='none'
                            >
                              <path
                                opacity='0.2'
                                d='M5.08398 17.3164C4.29336 16.5258 4.81758 14.8672 4.41367 13.8961C4.00977 12.925 2.46289 12.0742 2.46289 11C2.46289 9.92578 3.99258 9.10938 4.41367 8.10391C4.83477 7.09844 4.29336 5.47422 5.08398 4.68359C5.87461 3.89297 7.5332 4.41719 8.5043 4.01328C9.47539 3.60938 10.3262 2.0625 11.4004 2.0625C12.4746 2.0625 13.291 3.59219 14.2965 4.01328C15.302 4.43438 16.9262 3.89297 17.7168 4.68359C18.5074 5.47422 17.9832 7.13281 18.3871 8.10391C18.791 9.075 20.3379 9.92578 20.3379 11C20.3379 12.0742 18.8082 12.8906 18.3871 13.8961C17.966 14.9016 18.5074 16.5258 17.7168 17.3164C16.9262 18.107 15.2676 17.5828 14.2965 17.9867C13.3254 18.3906 12.4746 19.9375 11.4004 19.9375C10.3262 19.9375 9.50977 18.4078 8.5043 17.9867C7.49883 17.5656 5.87461 18.107 5.08398 17.3164Z'
                                fill='#FF4C51'
                              />
                              <path
                                d='M5.08398 17.3164C4.29336 16.5258 4.81758 14.8672 4.41367 13.8961C4.00977 12.925 2.46289 12.0742 2.46289 11C2.46289 9.92578 3.99258 9.10937 4.41367 8.10391C4.83477 7.09844 4.29336 5.47422 5.08398 4.68359C5.87461 3.89297 7.5332 4.41719 8.5043 4.01328C9.47539 3.60938 10.3262 2.0625 11.4004 2.0625C12.4746 2.0625 13.291 3.59219 14.2965 4.01328C15.302 4.43438 16.9262 3.89297 17.7168 4.68359C18.5074 5.47422 17.9832 7.13281 18.3871 8.10391C18.791 9.075 20.3379 9.92578 20.3379 11C20.3379 12.0742 18.8082 12.8906 18.3871 13.8961C17.966 14.9016 18.5074 16.5258 17.7168 17.3164C16.9262 18.107 15.2676 17.5828 14.2965 17.9867C13.3254 18.3906 12.4746 19.9375 11.4004 19.9375C10.3262 19.9375 9.50977 18.4078 8.5043 17.9867C7.49883 17.5656 5.87461 18.107 5.08398 17.3164Z'
                                stroke='#FF4C51'
                                stroke-width='2'
                                stroke-linecap='round'
                                stroke-linejoin='round'
                              />
                              <path
                                d='M14.6504 8L8.65039 14'
                                stroke='#FF4C51'
                                stroke-width='1.5'
                                stroke-linecap='round'
                                stroke-linejoin='round'
                              />
                              <path
                                d='M8.65039 8L14.6504 14'
                                stroke='#FF4C51'
                                stroke-width='1.5'
                                stroke-linecap='round'
                                stroke-linejoin='round'
                              />
                            </svg>
                          ) : dataBalanceTransactionDept?.data?.return_wages_disbursement?.ewa_disbursement <
                            dataBalanceTransactionDept?.data?.return_wages_disbursement?.reconciled ? (
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='23'
                              height='22'
                              viewBox='0 0 23 22'
                              fill='none'
                            >
                              <path
                                opacity='0.2'
                                d='M5.08398 17.3164C4.29336 16.5258 4.81758 14.8672 4.41367 13.8961C4.00977 12.925 2.46289 12.0742 2.46289 11C2.46289 9.92578 3.99258 9.10938 4.41367 8.10391C4.83477 7.09844 4.29336 5.47422 5.08398 4.68359C5.87461 3.89297 7.5332 4.41719 8.5043 4.01328C9.47539 3.60938 10.3262 2.0625 11.4004 2.0625C12.4746 2.0625 13.291 3.59219 14.2965 4.01328C15.302 4.43438 16.9262 3.89297 17.7168 4.68359C18.5074 5.47422 17.9832 7.13281 18.3871 8.10391C18.791 9.075 20.3379 9.92578 20.3379 11C20.3379 12.0742 18.8082 12.8906 18.3871 13.8961C17.966 14.9016 18.5074 16.5258 17.7168 17.3164C16.9262 18.107 15.2676 17.5828 14.2965 17.9867C13.3254 18.3906 12.4746 19.9375 11.4004 19.9375C10.3262 19.9375 9.50977 18.4078 8.5043 17.9867C7.49883 17.5656 5.87461 18.107 5.08398 17.3164Z'
                                fill='#28C76F'
                              />
                              <path
                                d='M15.1816 8.9375L10.1371 13.75L7.61914 11.3438M5.08398 17.3164C4.29336 16.5258 4.81758 14.8672 4.41367 13.8961C4.00977 12.925 2.46289 12.0742 2.46289 11C2.46289 9.92578 3.99258 9.10938 4.41367 8.10391C4.83477 7.09844 4.29336 5.47422 5.08398 4.68359C5.87461 3.89297 7.5332 4.41719 8.5043 4.01328C9.47539 3.60938 10.3262 2.0625 11.4004 2.0625C12.4746 2.0625 13.291 3.59219 14.2965 4.01328C15.302 4.43438 16.9262 3.89297 17.7168 4.68359C18.5074 5.47422 17.9832 7.13281 18.3871 8.10391C18.791 9.075 20.3379 9.92578 20.3379 11C20.3379 12.0742 18.8082 12.8906 18.3871 13.8961C17.966 14.9016 18.5074 16.5258 17.7168 17.3164C16.9262 18.107 15.2676 17.5828 14.2965 17.9867C13.3254 18.3906 12.4746 19.9375 11.4004 19.9375C10.3262 19.9375 9.50977 18.4078 8.5043 17.9867C7.49883 17.5656 5.87461 18.107 5.08398 17.3164Z'
                                stroke='#28C76F'
                                stroke-width='2'
                                stroke-linecap='round'
                                stroke-linejoin='round'
                              />
                            </svg>
                          ) : (
                            ''
                          )}
                        </td>
                        <td className='px-6 py-4 text-left text-[15px] font-normal leading-[22px]'>
                          {dataBalanceTransactionDept
                            ? __n(dataBalanceTransactionDept?.data?.return_wages_disbursement?.reconciled, 'đ')
                            : '-'}
                        </td>
                        <td className='px-6 py-4 text-left text-[15px] font-normal leading-[22px]'>
                          {dataBalanceTransactionDept
                            ? __n(dataBalanceTransactionDept?.data?.return_wages_disbursement?.remaining, 'đ')
                            : '-'}
                        </td>
                        <td className='px-6 py-4'>
                          <FormInput
                            name='debt_disbursement'
                            className='rounded-full w-full'
                            control={methods.control}
                            placeholder={__('Enter Value')}
                            disabled={!methods.getValues('company_id')}
                          />
                        </td>
                      </tr>
                      {wageDisbursementDifference > 0 && (
                        <tr className=' border-gray-100'>
                          <td className='px-6 py-4 text-[15px] font-normal leading-[22px] w-[50px]'>4</td>
                          <td className='px-6 py-4 text-[15px] font-normal leading-[22px] '>{__('Different')}</td>
                          <td className='p-4 text-[15px] font-normal leading-[22px] flex gap-2 flex-col items-start'>
                            <Chip
                              label={__(`Employer ${employerPercentage}% `)}
                              className={`bg-[#D6F4F7] rounded-full ${employerPercentage > 0 ? 'flex' : 'hidden'}  text-[#00BAD1] text-[13px] font-medium leading-[18px]`}
                            />
                            <Chip
                              label={__(`Lift ${liftPercentage}% `)}
                              className={`bg-[#DEE3FD] rounded-full ${liftPercentage > 0 ? 'flex' : 'hidden'} text-[#3551F2] text-[13px] font-medium leading-[18px] `}
                            />
                          </td>
                          <td className='px-6 py-4 text-left text-[15px] font-normal leading-[22px]'>
                            {__n(dataBalanceTransactionDept?.data?.different?.reconciled, 'đ')}
                          </td>
                          <td className='px-6 py-4 text-left text-[15px] font-normal leading-[22px]'>
                            {__n(dataBalanceTransactionDept?.data?.different?.remaining, 'đ')}
                          </td>
                          <td className='px-6 py-4'>
                            <FormInput
                              name='different'
                              className='rounded-full w-full'
                              control={methods.control}
                              disabled={!dataBalanceTransactionDept?.data?.different.is_allow_pay}
                              placeholder={__('Enter Value')}
                            />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Box>
              <Box className='flex flex-col gap-4 rounded-[20px] bg-[#F3F2F3] p-6'>
                <Typography className='text-[15px] font-normal leading-[22px]'>
                  {__(`Total: 1 + 2 + 3 ${wageDisbursementDifference > 0 ? ' + 4' : ''}`)}
                </Typography>
                <Typography className='text-[46px] font-medium leading-[68px]'>{__n(total, 'đ')}</Typography>
              </Box>
            </Box>
            <div className='flex flex-row gap-4 mt-6'>
              <Button
                variant='contained'
                className='text-white text-[15px] font-medium leading-[22px] bg-[#3551F2] shadow-none rounded-full'
                type='submit'
                disabled={
                  !methods.formState.isValid ||
                  dataBalanceTransactionDept?.data === null ||
                  !dataBalanceTransactionDept?.data?.is_allow_create_balance
                }
              >
                {__('Submit')}
              </Button>
              <Button
                variant='contained'
                className='text-[#FF4C51] text-[15px] font-medium leading-[22px] bg-[#FCE2E3] shadow-none rounded-full'
                onClick={handleClose}
              >
                {__('Cancel')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Drawer>
  )
}
